// packages/shared/src/customFieldDefs.ts
//
// W1 custom-fields (#372 r2 / #373 r5), slice S1. The shared source-of-truth for
// tenant-definable custom fields: the `CustomFieldDef` shape, the frozen shipped
// catalogue, the precedence resolver (mirrors `resolveNewBookingForm`), and the
// two server-side validators (def-shape at the config PATCH; value-shape at every
// persist site). Reused FE + BE so neither can drift.
//
// Design contract: DESIGN.md Q1 (catalogue on LocationConfig.customFieldDefs;
// resolve mirrors resolveNewBookingForm — absent/empty/malformed → DEFAULT), Q2
// (canonical value key = immutable camelCase `key`; `id` is stable def identity),
// Q3 (legacy `cfScope:'booking'` normalises to `scope:'reservation'` on resolve),
// Q6 (fail-closed validateCustomFields: def-matched keys enforce required/type/
// options; UNCONFIGURED keys pass as bounded free-form to keep the CSV __custom__
// path — AC-16 — and forward-compat writes working).

export const CUSTOM_FIELD_TYPES = [
  'text', 'email', 'phone', 'number', 'select', 'multiselect', 'radio',
  'checkbox', 'textarea', 'date', 'time',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/** Choice types whose def MUST carry a non-empty `options` list (AC-4). */
export const CHOICE_FIELD_TYPES = ['select', 'multiselect', 'radio'] as const;

export const CUSTOM_FIELD_SCOPES = ['contact', 'reservation'] as const;
export type CustomFieldScope = (typeof CUSTOM_FIELD_SCOPES)[number];

/** Upper bound on a single stored custom value's string length (bounded free-form). */
export const CUSTOM_FIELD_VALUE_MAX_LEN = 5000;

/** Upper bound on the number of keys in one value bag (per scope). Bounds an
 *  abusive/oversized bag independent of the def catalogue — a crafted request can
 *  carry unconfigured free-form keys, so the count cap protects the item size
 *  even when few (or no) defs are configured. */
export const MAX_CUSTOM_FIELD_VALUES = 200;

/** Upper bound on the aggregate coerced size (chars) of one value bag — a
 *  belt-and-suspenders guard on total bag weight toward the 400KB item cap. */
export const MAX_CUSTOM_FIELD_BAG_LEN = 50_000;

/** Keys that must never appear in a value bag — writing them risks prototype
 *  pollution (JSON.parse yields an OWN enumerable `__proto__` that survives a
 *  round-trip) or shadows built-ins. Rejected fail-closed at every persist site. */
export const RESERVED_VALUE_KEYS = ['__proto__', 'constructor', 'prototype'] as const;

// Catalogue cap — the defs array rides the hot-read LocationConfig singleton
// (shared 400KB item with events/automations/brandConfig), so it must stay
// bounded (G3 dynamo LOW-1, 2026-07-15).
export const MAX_CUSTOM_FIELD_DEFS = 100;

export interface CustomFieldOption {
  value: string;
  label: string;
}

export interface CustomFieldVisibleIf {
  dependsOn: string;
  in?: unknown[];
  isTruthy?: boolean;
}

export interface CustomFieldDef {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  scope: CustomFieldScope;
  options?: CustomFieldOption[] | string[];
  required?: boolean;
  special?: boolean;
  /** Optional field-level default value, folded into the create-form mount seed
   *  (useConfigDrivenForm.applyFieldDefaults). Validated ONLY against the def's
   *  own per-type/options check — a select default must be one of its options. */
  default?: string;
  visibleIf?: CustomFieldVisibleIf;
  visibility?: string[];
  fieldRole?: 'input' | 'display';
  order?: number;
  // Legacy alias accepted on stored blobs; the resolver normalises it to `scope`.
  cfScope?: 'contact' | 'booking';
}

export interface CustomFieldValidationResult {
  ok: boolean;
  errors: string[];
}

/** Sources the resolver reads, mirroring resolveNewBookingForm's chain. */
export interface CustomFieldDefSources {
  config?: { customFieldDefs?: unknown } | null;
  appConfig?: { customFieldDefs?: unknown } | null;
}

/** Recursively freeze an object graph so no shipped default can be mutated in
 *  place — the array, each def, and each nested options object/predicate. */
function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as Record<string, unknown>)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

// The frozen shipped catalogue — carries r5's (#373 S5) booking-type toggle so a
// CATALOGUE-LESS tenant gets it from DEFAULT via resolveCustomFieldDefs:
//   • bookingType — select, scope 'reservation', Individual|Company/Business,
//                   default 'individual'.
//   • companyName — text, scope 'contact', gated by the SAME shared `visibleIf`
//                   predicate ({dependsOn:'bookingType', in:['company']}).
// Deep-frozen so a shipped default can never be mutated in place, and so a stored
// per-tenant catalogue is never retro-injected with a later default (snapshot
// semantics — AC-34, the #367 class).
export const DEFAULT_CUSTOM_FIELD_DEFS: CustomFieldDef[] = deepFreeze([
  {
    id: 'bookingType',
    key: 'bookingType',
    label: 'Booking type',
    type: 'select',
    scope: 'reservation',
    options: [
      { value: 'individual', label: 'Individual' },
      { value: 'company', label: 'Company/Business' },
    ],
    default: 'individual',
  },
  {
    id: 'companyName',
    key: 'companyName',
    label: 'Company name',
    type: 'text',
    scope: 'contact',
    visibleIf: { dependsOn: 'bookingType', in: ['company'] },
  },
] as CustomFieldDef[]);

// ── resolveCustomFieldDefs — precedence + drift-safety (AC-33/34/35) ──────────
// Mirrors resolveNewBookingForm: appConfig source wins over the config source; a
// stored non-empty array is authoritative; absent / empty / malformed falls
// through to the shipped DEFAULT (the #149 empty→DEFAULT guard). Legacy
// `cfScope:'booking'` is normalised to `scope:'reservation'` (Q3).
export function resolveCustomFieldDefs(sources?: CustomFieldDefSources): CustomFieldDef[] {
  const candidates = [sources?.appConfig, sources?.config];

  for (const src of candidates) {
    if (!src || typeof src !== 'object') continue;
    const stored = (src as { customFieldDefs?: unknown }).customFieldDefs;
    // Malformed (non-array) or empty → fall through to the next candidate, never
    // straight to DEFAULT while a valid blob may still exist later in the chain.
    if (!Array.isArray(stored) || stored.length === 0) continue;
    return stored.map(normalizeDef);
  }

  return DEFAULT_CUSTOM_FIELD_DEFS;
}

/**
 * Normalise a stored def: fold the legacy `cfScope` alias into `scope`, and
 * give every def an EXPLICIT scope.
 *
 * #890 — why the default exists. Two consumers disagreed about what an unset
 * scope meant, and a venue's fields quietly stopped printing:
 *
 *   NewBookingDialog.jsx:123  `def.scope === "contact" ? "contact" : "reservation"`
 *                             → unset behaves as RESERVATION, the field renders on
 *                               the booking form, and useBookingSubmit.js:54 writes
 *                               its value into the RESERVATION value bag.
 *   PrintDialog.jsx:105       `d.scope === "reservation"` (strict)
 *                             → the same def is DROPPED from the printed sheet.
 *
 * So staff filled the field in on every booking and it was simply missing from
 * the day sheet. Resolving the default HERE is what makes every consumer agree.
 * (At #890 relaxing the printout's scope filter was forbidden as a recorded
 * privacy guarantee. That is no longer so: Rob 2026-10-01 amended
 * ADR-2026-07-15 #2 — guest-scoped NON-special fields now print under the same
 * opt-in, read from the guest bag. `special` still never prints.)
 *
 * Defaulting to `reservation` follows where the value is ALREADY stored.
 * `special: true` remains the fail-safe and is untouched, and an explicit
 * `scope: 'contact'` is always honoured.
 *
 * AN EARLIER VERSION OF THIS NOTE CLAIMED THE DEFAULT "does not move contact
 * data onto paper", AND THAT WAS FALSE when written — caught at G3. The print
 * surface filtered the DEF LIST by scope but read each cell from a UNION of the
 * contact and reservation bags, so the scope test was a filter on names, not on
 * data, and re-labelling a def would have printed everything that def had ever
 * written onto the guest record. `PrintDialog.jsx` now reads ONE bag per
 * column, chosen by the def's scope (reservation → the booking's bag, contact →
 * the guest's bag), with no fallback to the other bag in either direction —
 * which is what makes the sentence above true. If a consumer ever merges the
 * bags again, this default becomes an exposure, not a fix.
 */
function normalizeDef(raw: unknown): CustomFieldDef {
  // `cfScope` is WIDENED to string for this local, deliberately. The public type
  // narrows it to 'contact' | 'booking' because those are the only values we ever
  // MEANT to write — but this function takes `unknown` from a tenant's stored
  // blob, and the whole reason it exists is that real data contains values the
  // type says are impossible (someone wrote the new word 'reservation' into the
  // legacy field). Comparing against the narrow union would be a type error on
  // the exact case being fixed.
  const def = { ...(raw as Record<string, unknown>) } as unknown as Omit<CustomFieldDef, 'cfScope'> & {
    cfScope?: string;
  };
  if (def.cfScope !== undefined) {
    if (def.scope === undefined) {
      // 'booking' is the legacy word for 'reservation'. The old fold was
      // `=== 'booking' ? 'reservation' : 'contact'`, so anyone who wrote the
      // NEW word into the LEGACY field had their def silently reclassified as
      // contact-scoped and dropped from print for good — with no error, because
      // 'contact' is a perfectly valid scope.
      def.scope =
        def.cfScope === 'booking' || def.cfScope === 'reservation'
          ? 'reservation'
          : 'contact';
    }
    delete def.cfScope;
  }
  // No scope recorded at all ⇒ reservation, per the header note. An explicitly
  // set but UNRECOGNISED cfScope is folded to 'contact' above, because an
  // unreadable intent is not the same as no intent. Since the 2026-10-01 ADR
  // amendment that is no longer "kept off paper": a non-special contact def
  // prints under the opt-in, read from the GUEST bag only. It stays safe only
  // while print never merges the bags — merging them again would become an
  // exposure.
  if (def.scope === undefined) def.scope = 'reservation';
  return def as CustomFieldDef;
}

// ── validateCustomFieldDefs — DEFINITION-shape validation (AC-3/AC-4/AC-23) ────
// Rides the config PATCH handler like validateAutomations. Rejects a non-array; a
// def missing id/key/label/type/scope; an out-of-range type/scope; a choice-type
// def with no options; a non-boolean `special`; and duplicate id/key within the
// same catalogue (a value bag is keyed by `key`, so a dup would silently shadow).
export function validateCustomFieldDefs(input: unknown): CustomFieldValidationResult {
  if (!Array.isArray(input)) return { ok: false, errors: ['customFieldDefs must be an array'] };
  if (input.length > MAX_CUSTOM_FIELD_DEFS) {
    return { ok: false, errors: [`customFieldDefs exceeds the ${MAX_CUSTOM_FIELD_DEFS}-definition limit`] };
  }

  const errors: string[] = [];
  const seenKeys = new Set<string>();
  const seenIds = new Set<string>();

  input.forEach((raw, i) => {
    const p = `customFieldDefs[${i}]`;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      errors.push(`${p} must be an object`);
      return;
    }
    const d = raw as Record<string, unknown>;

    for (const req of ['id', 'key', 'label'] as const) {
      if (typeof d[req] !== 'string' || (d[req] as string).trim().length === 0) {
        errors.push(`${p}.${req} is required`);
      }
    }

    if (typeof d.type !== 'string' || !(CUSTOM_FIELD_TYPES as readonly string[]).includes(d.type)) {
      errors.push(`${p}.type must be one of: ${CUSTOM_FIELD_TYPES.join(', ')}`);
    }

    if (typeof d.scope !== 'string' || !(CUSTOM_FIELD_SCOPES as readonly string[]).includes(d.scope)) {
      errors.push(`${p}.scope must be one of: ${CUSTOM_FIELD_SCOPES.join(', ')}`);
    }

    // Choice types require a non-empty options list.
    if ((CHOICE_FIELD_TYPES as readonly string[]).includes(d.type as string)) {
      if (!Array.isArray(d.options) || d.options.length === 0) {
        errors.push(`${p}.options must be a non-empty list for a ${d.type} field`);
      }
    }

    if (d.special !== undefined && typeof d.special !== 'boolean') {
      errors.push(`${p}.special must be a boolean`);
    }

    // A field-level `default`, when present, must satisfy the def's OWN per-type /
    // options check (a select default outside its options → invalid). Reuses the
    // value-side type validator so def-shape and value validation never diverge.
    if (d.default !== undefined) {
      if (typeof d.default !== 'string') {
        errors.push(`${p}.default must be a string`);
      } else {
        const defErr = validateTypedValue(d as unknown as CustomFieldDef, d.default);
        if (defErr) errors.push(`${p}.default is invalid — ${defErr}`);
      }
    }

    if (typeof d.key === 'string') {
      if (seenKeys.has(d.key)) errors.push(`${p}.key "${d.key}" is duplicated`);
      seenKeys.add(d.key);
    }
    if (typeof d.id === 'string') {
      if (seenIds.has(d.id)) errors.push(`${p}.id "${d.id}" is duplicated`);
      seenIds.add(d.id);
    }
  });

  return errors.length ? { ok: false, errors } : { ok: true, errors: [] };
}

// ── Bag limit check + typed rejection (S2 G3 dynamo MEDIUM, 2026-07-15) ───────

/** Typed rejection for a MERGED bag that breaches the limits — thrown by the
 *  persist layer (dynamo/contacts.update) after its read-merge and mapped to a
 *  400 at the routes. Matched by `code`, which survives module duplication
 *  where `instanceof` may not. */
export class CustomFieldsBagLimitError extends Error {
  code = 'CUSTOM_FIELDS_BAG_LIMIT' as const;
  constructor(message: string) {
    super(message);
    this.name = 'CustomFieldsBagLimitError';
  }
}

/** Check a value bag against the count + aggregate-weight caps. Returns the
 *  violation message, or null when bounded. Shared by validateCustomFields (the
 *  per-request check) and the persist layer's post-merge guard: the request-
 *  level caps alone cannot bound the DURABLE bag, because the shallow-merge
 *  accretes keys across requests toward DynamoDB's 400KB item cap. */
export function customFieldBagLimitError(bag: Record<string, unknown>): string | null {
  const keys = Object.keys(bag);
  if (keys.length > MAX_CUSTOM_FIELD_VALUES) {
    return `custom field bag exceeds the ${MAX_CUSTOM_FIELD_VALUES}-value limit`;
  }
  let bagLen = 0;
  for (const key of keys) {
    const v = bag[key];
    if (v == null) continue;
    bagLen += typeof v === 'string' ? v.length : JSON.stringify(v).length;
  }
  if (bagLen > MAX_CUSTOM_FIELD_BAG_LEN) {
    return `custom field bag exceeds the ${MAX_CUSTOM_FIELD_BAG_LEN}-character total limit`;
  }
  return null;
}

// ── validateCustomFields — server-side VALUE validation (Q6, AC-16/18/19/20/21) ─
// Fail-closed at every persist site. For each def in the GIVEN scope, enforce
// required-non-empty, type format, and option membership against the value in the
// bag (keyed by the def's immutable `key` — AC-5). Keys in the bag WITHOUT a
// matching def in this scope pass through as bounded free-form (string-coerced,
// size-capped) — required to keep the CSV `__custom__` path (AC-16) and
// forward-compat writes working; there is no def to type/option-check them against.
//
// `opts.partial` (update-path semantics — S2 G3 verifier carry d): required-ness
// only binds keys the request actually CARRIES — an absent key is satisfied by
// the stored bag, which the persist layer's read-merge preserves. A present-but-
// blank required value still rejects: a required field can be omitted from a
// partial update, never blanked. Create paths use the default (full-bag) mode.
export function validateCustomFields(
  defs: CustomFieldDef[],
  bag: Record<string, unknown>,
  scope: CustomFieldScope,
  opts?: { partial?: boolean },
): CustomFieldValidationResult {
  const errors: string[] = [];
  const values = bag && typeof bag === 'object' && !Array.isArray(bag) ? bag : {};
  const scopedDefs = Array.isArray(defs) ? defs.filter((d) => d && d.scope === scope) : [];
  const defByKey = new Map(scopedDefs.map((d) => [d.key, d]));

  const bagKeys = Object.keys(values);

  // 0. Bag-shape hardening (S1 G3 security LOW — enforced HERE so every persist
  //    path, incl. the contacts PUT, inherits it). Reject reserved/pollution keys,
  //    an over-count bag, and an over-weight bag before any per-def work.
  for (const key of bagKeys) {
    if ((RESERVED_VALUE_KEYS as readonly string[]).includes(key)) {
      errors.push(`${key} is a reserved key and may not be used as a custom field`);
    }
  }
  const limitErr = customFieldBagLimitError(values);
  if (limitErr) errors.push(limitErr);
  // Short-circuit: a poisoned/oversized bag never reaches per-value checks.
  if (errors.length) return { ok: false, errors };

  // 1. Enforce each configured def against its value in the bag.
  for (const def of scopedDefs) {
    // Partial mode: an absent key is out of this request's scope entirely.
    if (opts?.partial && !Object.prototype.hasOwnProperty.call(values, def.key)) continue;

    const value = values[def.key];

    // A non-primitive value on a scalar (non-multiselect) def is invalid up front:
    // an array/object would otherwise slip past isEmptyValue([]) → the Number([])
    // ===0 coercion hole for number-typed defs, or be silently string-coerced.
    if (value != null && typeof value === 'object' && def.type !== 'multiselect') {
      errors.push(`${def.key} must be a primitive value`);
      continue;
    }

    const empty = isEmptyValue(value);
    if (empty) {
      if (def.required) errors.push(`${def.key} is required`);
      continue; // a blank non-required field is absent, never type-checked (AC-11).
    }

    const typeErr = validateTypedValue(def, value);
    if (typeErr) errors.push(typeErr);
  }

  // 2. Unconfigured keys pass as bounded free-form (string-coerced, size-capped).
  for (const [key, value] of Object.entries(values)) {
    if (defByKey.has(key)) continue;
    if (value == null) continue;
    const coerced = typeof value === 'string' ? value : JSON.stringify(value);
    if (coerced.length > CUSTOM_FIELD_VALUE_MAX_LEN) {
      errors.push(`${key} exceeds the ${CUSTOM_FIELD_VALUE_MAX_LEN}-character limit`);
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true, errors: [] };
}

/** A value counts as "empty" (absent) if it's nullish, a blank string, or []. */
function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Allowed option `value`s for a choice def (options may be objects or strings). */
function optionValues(def: CustomFieldDef): string[] {
  if (!Array.isArray(def.options)) return [];
  return def.options.map((o) => (typeof o === 'string' ? o : o?.value)).filter((v): v is string => v != null);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s().-]{6,20}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/;

/** Validate a non-empty value against its def's type. Returns an error or null. */
function validateTypedValue(def: CustomFieldDef, value: unknown): string | null {
  switch (def.type) {
    case 'select':
    case 'radio': {
      const allowed = optionValues(def);
      if (typeof value !== 'string' || !allowed.includes(value)) {
        return `${def.key} must be one of: ${allowed.join(', ')}`;
      }
      return null;
    }
    case 'multiselect': {
      const allowed = optionValues(def);
      const members = Array.isArray(value) ? value : [value];
      const bad = members.filter((m) => typeof m !== 'string' || !allowed.includes(m));
      return bad.length ? `${def.key} must be a subset of: ${allowed.join(', ')}` : null;
    }
    case 'email':
      return typeof value === 'string' && EMAIL_RE.test(value) ? null : `${def.key} must be a valid email`;
    case 'phone':
      return typeof value === 'string' && PHONE_RE.test(value) ? null : `${def.key} must be a valid phone number`;
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? null : `${def.key} must be a number`;
    }
    case 'date':
      return typeof value === 'string' && DATE_RE.test(value) ? null : `${def.key} must be a date (yyyy-mm-dd)`;
    case 'time':
      return typeof value === 'string' && TIME_RE.test(value) ? null : `${def.key} must be a time (hh:mm)`;
    // text / textarea / checkbox — bounded free-form; cap the string length.
    default: {
      const coerced = typeof value === 'string' ? value : String(value);
      return coerced.length > CUSTOM_FIELD_VALUE_MAX_LEN
        ? `${def.key} exceeds the ${CUSTOM_FIELD_VALUE_MAX_LEN}-character limit`
        : null;
    }
  }
}
